import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';
import { logError, logInfo } from '@/utils/errorHandler';
import { API_ENDPOINTS } from '@/config/config';

// Platform interface for dynamic platform support
export interface PlatformOption {
  value: string;
  label: string;
  description: string;
}

export interface ExternalPlatformConfig {
  id: number;
  org: number;
  platform: string; // Changed from union type to string for flexibility
  name: string;
  is_enabled: boolean;
  settings: Record<string, any>;
  // Scheduling fields
  auto_sync_enabled: boolean;
  sync_interval: 'disabled' | 'hourly' | 'daily' | 'weekly' | 'monthly';
  last_auto_sync_at?: string;
  next_sync_at?: string;
  created_at: string;
  updated_at: string;
  last_synced_at?: string;
}

export interface ProductSyncLog {
  id: number;
  org: number;
  product?: {
    id: number;
    name: string;
    sku: string;
  };
  product_id: number;
  platform: string; // Changed from union type to string for flexibility
  operation: 'create' | 'update' | 'delete' | 'inventory_update';
  success: boolean;
  external_id?: string;
  timestamp: string;
  details: Record<string, any>;
}

export interface CreatePlatformConfigRequest {
  platform: string; // Changed from union type to string for flexibility
  name: string;
  settings: Record<string, any>;
  is_enabled: boolean;
}

export interface UpdatePlatformConfigRequest {
  name: string;
  settings: Record<string, any>;
  is_enabled: boolean;
  // Scheduling fields
  auto_sync_enabled?: boolean;
  sync_interval?: 'disabled' | 'hourly' | 'daily' | 'weekly' | 'monthly';
}

// Hook to fetch available platforms
export function useAvailablePlatforms() {
  return useQuery({
    queryKey: ['available-platforms'],
    queryFn: async (): Promise<PlatformOption[]> => {
      try {
        const response = await api.get('/api/external-platform-configs/available-platforms/');
        return response.data.platforms || [];
      } catch (error: any) {
        logError(
          'Failed to fetch available platforms',
          {
            component: 'useConnectorManagement',
            action: 'useAvailablePlatforms',
            metadata: {
              error: error.message,
              response: error.response?.data,
              status: error.response?.status,
            },
          },
          { showToast: false, userFacing: false }
        );
        throw error;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - platforms don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useConnectorConfigs() {
  return useQuery({
    queryKey: ['connector-configs'],
    queryFn: async (): Promise<ExternalPlatformConfig[]> => {
      try {
        const response = await api.get('/api/external-platform-configs/');
        return response.data.results || response.data;
      } catch (error: any) {
        logError(
          'Failed to fetch connector configurations',
          {
            component: 'useConnectorManagement',
            action: 'useConnectorConfigs',
            metadata: {
              error: error.message,
              response: error.response?.data,
              status: error.response?.status,
            },
          },
          { showToast: false, userFacing: false }
        );
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useSyncLogs(page: number = 1, pageSize: number = 20) {
  return useQuery({
    queryKey: ['sync-logs', page, pageSize],
    queryFn: async (): Promise<{
      results: ProductSyncLog[];
      count: number;
      page: number;
      page_size: number;
      total_pages: number;
    }> => {
      try {
        // Get logs from all platform configs with pagination
        const response = await api.get('/api/external-platform-configs/');
        const configs = response.data.results || response.data;

        logInfo('Fetching paginated sync logs', {
          component: 'useConnectorManagement',
          action: 'useSyncLogs',
          metadata: { configCount: configs.length, page, pageSize },
        });

        // Fetch logs for each config and combine them
        const allLogs: ProductSyncLog[] = [];
        let totalCount = 0;

        for (const config of configs) {
          try {
            const logsResponse = await api.get(
              `/api/external-platform-configs/${config.id}/sync-logs/?page=${page}&page_size=${pageSize}`
            );
            const data = logsResponse.data;
            const logs = data.results || data;
            allLogs.push(...logs);
            totalCount += data.count || logs.length;

            logInfo(`Fetched logs for config ${config.id}`, {
              component: 'useConnectorManagement',
              action: 'useSyncLogs',
              metadata: { configId: config.id, platform: config.platform, logCount: logs.length },
            });
          } catch (error) {
            // Continue if one config fails
            logError(
              `Failed to fetch logs for config ${config.id}`,
              {
                component: 'useConnectorManagement',
                action: 'useSyncLogs',
                metadata: { configId: config.id, error },
              },
              { showToast: false, userFacing: false }
            );
          }
        }

        // Sort by timestamp descending
        const sortedLogs = allLogs.sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

        logInfo('Paginated sync logs fetched successfully', {
          component: 'useConnectorManagement',
          action: 'useSyncLogs',
          metadata: {
            totalLogs: allLogs.length,
            successfulLogs: sortedLogs.filter(log => log.success).length,
            page,
            pageSize,
          },
        });

        return {
          results: sortedLogs,
          count: totalCount,
          page,
          page_size: pageSize,
          total_pages: Math.ceil(totalCount / pageSize),
        };
      } catch (error) {
        logError(
          'Failed to fetch sync logs',
          {
            component: 'useConnectorManagement',
            action: 'useSyncLogs',
            metadata: { error },
          },
          { showToast: false, userFacing: false }
        );
        throw error;
      }
    },
    // More aggressive cache settings for development
    staleTime: process.env.NODE_ENV === 'development' ? 0 : 30 * 1000, // No cache in dev
    gcTime: process.env.NODE_ENV === 'development' ? 0 : 2 * 60 * 1000, // No cache in dev
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: process.env.NODE_ENV === 'development' ? 10000 : false, // Refetch every 10s in dev
  });
}

export function useCreateConnectorConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePlatformConfigRequest): Promise<ExternalPlatformConfig> => {
      try {
        // Get user's organization from localStorage or make a call to get it
        const token = localStorage.getItem('access_token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Get user info to extract organization_id
        const userResponse = await api.get(API_ENDPOINTS.auth.user);
        const organizationId =
          userResponse.data.organization_id ||
          userResponse.data.organization?.id ||
          userResponse.data.profile?.organization?.id;

        if (!organizationId) {
          throw new Error('No organization found for user');
        }

        // Add organization to the request data
        const requestData = {
          ...data,
          org: organizationId,
        };

        const response = await api.post('/api/external-platform-configs/', requestData);
        return response.data;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          'Failed to create platform configuration';
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connector-configs'] });
    },
    onError: (error: Error) => {
      logError(
        'Failed to create connector configuration',
        {
          component: 'useConnectorManagement',
          action: 'useCreateConnectorConfig',
          metadata: { error: error.message },
        },
        { showToast: true, userFacing: true }
      );
    },
  });
}

export function useUpdateConnectorConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdatePlatformConfigRequest;
    }): Promise<ExternalPlatformConfig> => {
      try {
        const response = await api.patch(`/api/external-platform-configs/${id}/`, data);
        return response.data;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to update platform configuration';
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connector-configs'] });
    },
    onError: (error: Error) => {
      logError(
        'Failed to update connector configuration',
        {
          component: 'useConnectorManagement',
          action: 'useUpdateConnectorConfig',
          metadata: { error: error.message },
        },
        { showToast: true, userFacing: true }
      );
    },
  });
}

export function useDeleteConnectorConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      try {
        await api.delete(`/api/external-platform-configs/${id}/`);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to delete platform configuration';
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connector-configs'] });
      queryClient.invalidateQueries({ queryKey: ['sync-logs'] });
    },
    onError: (error: Error) => {
      logError(
        'Failed to delete connector configuration',
        {
          component: 'useConnectorManagement',
          action: 'useDeleteConnectorConfig',
          metadata: { error: error.message },
        },
        { showToast: true, userFacing: true }
      );
    },
  });
}

export function useListProducts(configId: number) {
  return useQuery({
    queryKey: ['platform-products', configId],
    queryFn: async (): Promise<any> => {
      try {
        const response = await api.get(`/api/external-platform-configs/${configId}/list-products/`);
        return response.data;
      } catch (error: any) {
        logError(
          'Failed to fetch platform products',
          {
            component: 'useConnectorManagement',
            action: 'useListProducts',
            metadata: {
              configId,
              error: error.message,
              response: error.response?.data,
            },
          },
          { showToast: false, userFacing: false }
        );
        throw error;
      }
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useBulkSyncProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      configId: number
    ): Promise<{ detail: string; product_count: number; platform: string }> => {
      try {
        const response = await api.post(
          `/api/external-platform-configs/${configId}/sync-products/`
        );
        return response.data;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to start bulk sync';
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connector-configs'] });
      queryClient.invalidateQueries({ queryKey: ['sync-logs'] });
      queryClient.refetchQueries({ queryKey: ['sync-logs'] });
    },
    onError: (error: Error) => {
      logError(
        'Failed to start bulk sync',
        {
          component: 'useConnectorManagement',
          action: 'useBulkSyncProducts',
          metadata: { error: error.message },
        },
        { showToast: true, userFacing: true }
      );
    },
  });
}

export function useSyncAllProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (configId: number): Promise<any> => {
      try {
        const response = await api.post(
          `/api/external-platform-configs/${configId}/sync-all-products/`
        );
        return response.data;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to sync all products';
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connector-configs'] });
      queryClient.invalidateQueries({ queryKey: ['sync-logs'] });
      queryClient.refetchQueries({ queryKey: ['sync-logs'] });
    },
    onError: (error: Error) => {
      logError(
        'Failed to sync all products',
        {
          component: 'useConnectorManagement',
          action: 'useSyncAllProducts',
          metadata: { error: error.message },
        },
        { showToast: true, userFacing: true }
      );
    },
  });
}

export function useTestConnection() {
  return useMutation({
    mutationFn: async (configId: number): Promise<{ status: string; message: string }> => {
      try {
        // For now, we'll use a simple approach to test connection
        // In the future, you might want to add a dedicated test endpoint
        const response = await api.get(`/api/external-platform-configs/${configId}/`);
        if (response.data) {
          return { status: 'success', message: 'Connection test successful' };
        }
        throw new Error('Invalid response from server');
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail || error.response?.data?.message || 'Connection test failed';
        throw new Error(errorMessage);
      }
    },
    onError: (error: Error) => {
      logError(
        'Connection test failed',
        {
          component: 'useConnectorManagement',
          action: 'useTestConnection',
          metadata: { error: error.message },
        },
        { showToast: true, userFacing: true }
      );
    },
  });
}

export function useSyncSingleProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      productId,
      platform,
    }: {
      productId: number;
      platform: string;
    }): Promise<{
      detail: string;
      product_sku: string;
      product_name: string;
      platform: string;
    }> => {
      try {
        const response = await api.post(`/api/products/${productId}/sync-to/${platform}/`);
        return response.data;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail || error.response?.data?.message || 'Failed to sync product';
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-logs'] });
    },
    onError: (error: Error) => {
      logError(
        'Failed to sync product',
        {
          component: 'useConnectorManagement',
          action: 'useSyncSingleProduct',
          metadata: { error: error.message },
        },
        { showToast: true, userFacing: true }
      );
    },
  });
}

export function useDebugProduct() {
  return useMutation({
    mutationFn: async (configId: number) => {
      try {
        const response = await api.post(
          `/api/external-platform-configs/${configId}/debug-product/`
        );
        return response.data;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to debug product data';
        throw new Error(errorMessage);
      }
    },
  });
}

export function useProductSyncStatus() {
  return useMutation({
    mutationFn: async ({
      productId,
      platform,
    }: {
      productId: number;
      platform: string;
    }): Promise<{
      product: { id: number; sku: string; name: string };
      platform: string;
      mapping: any;
      recent_logs: ProductSyncLog[];
      is_synced: boolean;
    }> => {
      try {
        const response = await api.get(`/api/products/${productId}/sync-status/${platform}/`);
        return response.data;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to get sync status';
        throw new Error(errorMessage);
      }
    },
    onError: (error: Error) => {
      logError(
        'Failed to get product sync status',
        {
          component: 'useConnectorManagement',
          action: 'useProductSyncStatus',
          metadata: { error: error.message },
        },
        { showToast: true, userFacing: true }
      );
    },
  });
}

export function useSyncProductToPlatforms() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      productId,
      platforms,
    }: {
      productId: number;
      platforms: string[];
    }): Promise<{
      product_id: number;
      results: Record<string, { success: boolean; message: string; external_id?: string }>;
    }> => {
      try {
        // Sync to each platform
        const results: Record<string, { success: boolean; message: string; external_id?: string }> =
          {};

        for (const platform of platforms) {
          try {
            const response = await api.post(`/api/products/${productId}/sync-to/${platform}/`);
            results[platform] = {
              success: true,
              message: response.data.detail || 'Sync successful',
              external_id: response.data.external_id,
            };
          } catch (error: any) {
            results[platform] = {
              success: false,
              message: error.response?.data?.detail || error.message || 'Sync failed',
            };
          }
        }

        return {
          product_id: productId,
          results,
        };
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to sync product to platforms';
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connector-configs'] });
      queryClient.invalidateQueries({ queryKey: ['sync-logs'] });
      queryClient.refetchQueries({ queryKey: ['sync-logs'] });
    },
    onError: (error: Error) => {
      logError(
        'Failed to sync product to platforms',
        {
          component: 'useConnectorManagement',
          action: 'useSyncProductToPlatforms',
          metadata: { error: error.message },
        },
        { showToast: true, userFacing: true }
      );
    },
  });
}

export function useAnalyticsSummary() {
  return useQuery({
    queryKey: ['analytics-summary'],
    queryFn: async () => {
      try {
        // Get analytics from all platform configs
        const response = await api.get('/api/external-platform-configs/');
        const configs = response.data.results || response.data;

        logInfo('Fetching analytics summary', {
          component: 'useConnectorManagement',
          action: 'useAnalyticsSummary',
          metadata: { configCount: configs.length },
        });

        // Fetch analytics for each config
        const allAnalytics = [];
        for (const config of configs) {
          try {
            const analyticsResponse = await api.get(
              `/api/external-platform-configs/${config.id}/analytics/`
            );
            allAnalytics.push(analyticsResponse.data);
          } catch (error) {
            // Continue if one config fails
            logError(
              `Failed to fetch analytics for config ${config.id}`,
              {
                component: 'useConnectorManagement',
                action: 'useAnalyticsSummary',
                metadata: { configId: config.id, error },
              },
              { showToast: false, userFacing: false }
            );
          }
        }

        // Aggregate analytics
        const totalSyncs = allAnalytics.reduce((sum, analytics) => sum + analytics.total_syncs, 0);
        const totalSuccessful = allAnalytics.reduce(
          (sum, analytics) => sum + analytics.successful_syncs,
          0
        );
        const overallSuccessRate = totalSyncs > 0 ? (totalSuccessful / totalSyncs) * 100 : 0;

        // Get all recent syncs
        const allRecentSyncs = allAnalytics.flatMap(analytics => analytics.recent_syncs);
        const sortedRecentSyncs = allRecentSyncs
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .slice(0, 10); // Show last 10 across all platforms

        return {
          total_syncs: totalSyncs,
          successful_syncs: totalSuccessful,
          failed_syncs: totalSyncs - totalSuccessful,
          success_rate: Math.round(overallSuccessRate * 10) / 10, // Round to 1 decimal
          platforms_count: allAnalytics.length,
          recent_syncs: sortedRecentSyncs,
          by_platform: allAnalytics,
        };
      } catch (error) {
        logError(
          'Failed to fetch analytics summary',
          {
            component: 'useConnectorManagement',
            action: 'useAnalyticsSummary',
            metadata: { error },
          },
          { showToast: false, userFacing: false }
        );
        throw error;
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });
}
